import type { TransactionClaimsOut } from '@/api/claimedLines';

/** Claim-gap states for a finding's transaction, per the claimed-ITC
 *  design doc: fully claimed / partially claimed / nothing claimed. */
export type ClaimGapState = 'fully_claimed' | 'partially_claimed' | 'not_claimed';

/** Rounding slack when comparing the claimed total against the observed
 *  tax — filed returns round per line, so a two-cent shortfall is still
 *  "fully claimed", not a recoverable residue. Held in integer cents:
 *  float subtraction leaves dust (130 − 129.98 > 0.02) that would let the
 *  badge and the displayed gap disagree at the tolerance boundary. */
const FULLY_CLAIMED_TOLERANCE_CENTS = 2;

function parseAmount(value: string | null | undefined): number | null {
  if (value == null || value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toCents(amount: number): number {
  return Math.round(amount * 100);
}

/** Classify the gap between what was claimed on returns and what the
 *  engine observed on the GL row (`evidence['tax_amount']`).
 *
 *  - No deterministic claim lines (or a zero claimed total) → not claimed.
 *  - Claimed covers the observed tax (within a two-cent rounding
 *    tolerance) → fully claimed.
 *  - Anything in between → partially claimed.
 *
 *  When the observed tax is missing or unparseable there is no gap to
 *  state, but claim lines still exist — that renders as claimed rather
 *  than inventing a partial state from an unknown baseline. */
export function claimGapState(
  claims: TransactionClaimsOut | undefined,
  observedTax: string | null,
): ClaimGapState {
  const claimedTotal = parseAmount(claims?.claimed_total);
  if (claims === undefined || claims.lines.length === 0 || claimedTotal === null) {
    return 'not_claimed';
  }
  if (claimedTotal <= 0) return 'not_claimed';
  const observed = parseAmount(observedTax);
  if (observed === null || observed <= 0) return 'fully_claimed';
  return toCents(claimedTotal) >= toCents(observed) - FULLY_CLAIMED_TOLERANCE_CENTS
    ? 'fully_claimed'
    : 'partially_claimed';
}

/** The unclaimed remainder (observed − claimed), or null when either
 *  side is unknown or nothing remains. */
export function claimGapAmount(
  claims: TransactionClaimsOut | undefined,
  observedTax: string | null,
): number | null {
  const observed = parseAmount(observedTax);
  if (observed === null) return null;
  const claimedTotal = parseAmount(claims?.claimed_total) ?? 0;
  // Same cents-based comparison as claimGapState, so a rendered gap and a
  // fully-claimed badge can never coexist.
  const gapCents = toCents(observed) - toCents(claimedTotal);
  return gapCents > FULLY_CLAIMED_TOLERANCE_CENTS ? gapCents / 100 : null;
}

/** Era-aware source labels (INP-755): a Xero return line comes from a
 *  filed return; a QBO GST-detail line is accrued in the books but the
 *  (annual) return may not be filed yet — the audit trail must not
 *  overstate a book accrual as a filed claim. */
export function claimSourceLabel(source: string): string {
  switch (source) {
    case 'xero-return':
      return 'filed return';
    case 'qbo-gst-detail':
      return 'accrued in books (return not yet filed)';
    case 'manual':
      return 'manual entry';
    default:
      return source;
  }
}
